if (!customElements.get('wlsn-quantity-selector')) {
  class WLSNQuantity extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        loading: 'loading',
        loadingState: 'wlsn-loading-state',
        disabled: 'disabled',
        error: 'error',
        quantityIncrease: 'js-quantity-increase',
        quantityDecrease: 'js-quantity-decrease',
        quantityInput: 'js-quantity-input',
        quantityError: 'js-quantity-error',
      };

      this.isLoading = false;
      this.currentQuantity = parseInt(this.dataset.quantity) || 1;
      this.minQuantity = parseInt(this.dataset.min) || 1;
      this.maxQuantity = this.dataset.max ? parseInt(this.dataset.max) : null;
      this.errorTimeout = null;

      this.bindMethods = {
        handleIncrease: this._handleIncrease.bind(this),
        handleDecrease: this._handleDecrease.bind(this),
        handleInput: this._handleInput.bind(this),
      };

      if (window.WLSNHelpers) {
        this._debouncedUpdateQuantity = window.WLSNHelpers.debounce(this._updateQuantity.bind(this), 300);
      } else {
        this._debouncedUpdateQuantity = this._updateQuantity.bind(this);
      }
    }

    get elements() {
      return {
        increaseButton: this.querySelector(`.${this.classes.quantityIncrease}`),
        decreaseButton: this.querySelector(`.${this.classes.quantityDecrease}`),
        input: this.querySelector(`.${this.classes.quantityInput}`),
        errorElement: this.querySelector(`.${this.classes.quantityError}`),
      };
    }

    connectedCallback() {
      this._attachEventListeners();
      this._updateButtonStates();
      this._listenForCartErrors();
    }

    disconnectedCallback() {
      this._detachEventListeners();
      this._stopListeningForCartErrors();
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }
    }

    _attachEventListeners() {
      const { increaseButton, decreaseButton, input } = this.elements;

      if (increaseButton) {
        increaseButton.addEventListener('click', this.bindMethods.handleIncrease);
      }
      if (decreaseButton) {
        decreaseButton.addEventListener('click', this.bindMethods.handleDecrease);
      }
      if (input) {
        input.addEventListener('change', this.bindMethods.handleInput);
        input.addEventListener('input', this.bindMethods.handleInput);
      }
    }

    _detachEventListeners() {
      const { increaseButton, decreaseButton, input } = this.elements;

      if (increaseButton) {
        increaseButton.removeEventListener('click', this.bindMethods.handleIncrease);
      }
      if (decreaseButton) {
        decreaseButton.removeEventListener('click', this.bindMethods.handleDecrease);
      }
      if (input) {
        input.removeEventListener('change', this.bindMethods.handleInput);
        input.removeEventListener('input', this.bindMethods.handleInput);
      }
    }

    _handleIncrease() {
      if (this.isLoading) return;

      const newQuantity = this.currentQuantity + 1;

      // Check max limit
      if (this.maxQuantity && newQuantity > this.maxQuantity) {
        return;
      }

      const previousQuantity = this.currentQuantity;
      this.currentQuantity = newQuantity;

      // Update input display
      const { input } = this.elements;
      if (input) {
        input.value = newQuantity;
        input.dispatchEvent(new Event('change'));
      }

      this._updateButtonStates();
      this._debouncedUpdateQuantity(newQuantity, previousQuantity);
    }

    _handleDecrease() {
      if (this.isLoading) return;

      if (this.currentQuantity > this.minQuantity) {
        const newQuantity = this.currentQuantity - 1;
        const previousQuantity = this.currentQuantity;
        this.currentQuantity = newQuantity;

        // Update input display
        const { input } = this.elements;
        if (input) {
          input.value = newQuantity;
          input.dispatchEvent(new Event('change'));
        }

        this._updateButtonStates();
        this._debouncedUpdateQuantity(newQuantity, previousQuantity);
      }
    }

    _handleInput(event) {
      if (this.isLoading) return;

      let newQuantity = parseInt(event.target.value) || this.minQuantity;

      // Validate against min/max
      newQuantity = Math.max(this.minQuantity, newQuantity);
      if (this.maxQuantity) {
        newQuantity = Math.min(this.maxQuantity, newQuantity);
      }

      if (newQuantity !== this.currentQuantity) {
        const previousQuantity = this.currentQuantity;
        this.currentQuantity = newQuantity;

        // Update input if it was corrected
        if (parseInt(event.target.value) !== newQuantity) {
          event.target.value = newQuantity;
        }

        this._updateButtonStates();
        this._debouncedUpdateQuantity(newQuantity, previousQuantity);
      }
    }

    _updateQuantity(newQuantity, previousQuantity) {
      if (this.isLoading) return;
      window.WLSNHelpers.triggerEvent(this, 'quantity:change', {
        quantity: newQuantity,
        previousQuantity: previousQuantity,
      });
    }

    _updateButtonStates() {
      const { increaseButton, decreaseButton } = this.elements;

      // Update decrease button
      if (decreaseButton) {
        if (this.currentQuantity <= this.minQuantity) {
          decreaseButton.setAttribute('disabled', 'disabled');
        } else {
          decreaseButton.removeAttribute('disabled');
        }
      }

      // Update increase button
      if (increaseButton && this.maxQuantity) {
        if (this.currentQuantity >= this.maxQuantity) {
          increaseButton.setAttribute('disabled', 'disabled');
        } else {
          increaseButton.removeAttribute('disabled');
        }
      }
    }

    setLoadingState(loading) {
      this.isLoading = loading;
      const { increaseButton, decreaseButton, input } = this.elements;

      if (loading) {
        this.classList.add(this.classes.loading);
        this.classList.add(this.classes.loadingState);
        increaseButton?.setAttribute('disabled', 'disabled');
        decreaseButton?.setAttribute('disabled', 'disabled');
        input?.setAttribute('disabled', 'disabled');
      } else {
        this.classList.remove(this.classes.loading);
        this.classList.remove(this.classes.loadingState);
        increaseButton?.removeAttribute('disabled');
        decreaseButton?.removeAttribute('disabled');
        input?.removeAttribute('disabled');
        this._updateButtonStates();
      }
    }

    setQuantity(quantity) {
      const validQuantity = Math.max(this.minQuantity, parseInt(quantity) || this.minQuantity);
      this.currentQuantity = validQuantity;

      const { input } = this.elements;
      if (input) {
        input.value = validQuantity;
      }

      this._updateButtonStates();
    }

    showError(message, maxAvailable = null) {
      const { errorElement } = this.elements;
      if (!errorElement) return;

      // Clear any existing timeout
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }

      // If maxAvailable is provided, update quantity to that value
      if (maxAvailable !== null) {
        this.setQuantity(maxAvailable);
        this.maxQuantity = maxAvailable;
        this.dataset.max = maxAvailable;
      }

      // Show error message
      errorElement.textContent = message;
      this.classList.add(this.classes.error);

      // Auto-hide error after 10 seconds
      this.errorTimeout = setTimeout(() => {
        this.hideError();
      }, 10000);
    }

    hideError() {
      const { errorElement } = this.elements;
      if (!errorElement) return;

      errorElement.textContent = '';
      this.classList.remove(this.classes.error);

      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
        this.errorTimeout = null;
      }
    }

    _listenForCartErrors() {
      this._cartErrorHandler = (event) => {
        const { lineKey, error } = event.detail;
        const variantId = this.dataset.variantId;

        // Check if this error is related to this quantity selector
        if (lineKey && lineKey.includes(variantId)) {
          this.showError(error);
        }
      };

      document.addEventListener('wlsn:cart:update:error', this._cartErrorHandler);
    }

    _stopListeningForCartErrors() {
      if (this._cartErrorHandler) {
        document.removeEventListener('wlsn:cart:update:error', this._cartErrorHandler);
        this._cartErrorHandler = null;
      }
    }
  }

  customElements.define('wlsn-quantity-selector', WLSNQuantity);
}
